import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalHeader} from 'reactstrap';
import {Link} from 'react-router-dom';

import {CaretRight, ListNumbers, Plugs} from '../../icons';
import {GoogleSheetsLogo} from '../../logos/GoogleSheetsLogo';
import {Spinner} from '../../Spinner';
import {fetchCandidates} from '../api';
import {GlobalLinesFromAccountsModal, PLACEMENT_MAIN, placementOf} from './GlobalLinesFromAccountsModal';
import './pathZero.scss';

/**
 * Cuánto se espera a las cuentas antes de mostrar el modal con su spinner.
 *
 * El modal sólo puede saltearse cuando no hay cuentas, y eso se sabe recién con la respuesta: si se
 * dibujaba al click, en una agencia chica —la respuesta llega en lo que dura la animación de
 * entrada— el modal empezaba a aparecer y se cortaba para saltar al wizard (recorrido de fede,
 * 01/10, punto 1). Con la espera, el caso rápido no dibuja nada que después se deshaga; el lento,
 * que es una cartera grande y casi siempre tiene cuentas, sí muestra que algo está cargando.
 */
export const LOADER_DELAY_MS = 400;

// Qué puede ofrecer «Desde tus cuentas conectadas» (ver `fromAccountsState`).
export const FROM_ACCOUNTS_OWN = 'OWN';
export const FROM_ACCOUNTS_UNOWNED = 'UNOWNED';
export const FROM_ACCOUNTS_ALL_COVERED = 'ALL_COVERED';
export const FROM_ACCOUNTS_NONE = 'NONE';
export const FROM_ACCOUNTS_UNKNOWN = 'UNKNOWN';

/**
 * El estado del camino «Desde tus cuentas conectadas»: `{kind, count, enabled}`.
 *
 * Cuenta lo mismo que después ofrece el modal de cuentas (`accountsFor` en
 * `GlobalLinesFromAccountsModal`): las del cliente y las que no tienen cliente. Antes contaba sólo
 * las del cliente, así que una agencia con decenas de cuentas sin dueño veía el camino apagado con
 * «este cliente no tiene cuentas» y no llegaba nunca al buscador que resolvía justamente eso
 * (Bakián, cliente AVIS, punto 2).
 *
 * - `OWN`: hay cuentas del cliente andando y sin línea, las que el modal pone en su lista principal.
 *   Es el caso de siempre.
 * - `UNOWNED`: no hay propias andando, pero la agencia tiene cuentas sin cliente: el modal abre con
 *   «¿Falta una cuenta?» desplegado, que es donde se conectan. Una propia dormida o cerrada no
 *   cuenta como «andando»: el modal la manda al cajón, y con ella sola la lista principal quedaba
 *   vacía y el buscador cerrado (review C-rec-fe-linea-08).
 * - `OWN` también si lo único propio es dormido o cerrado y no hay cuentas sin cliente: el modal
 *   las ofrece en su cajón («Usar igual»).
 * - `ALL_COVERED`: el cliente tiene cuentas y todas ya tienen su línea. Decir «no tiene cuentas»
 *   ahí era mentir.
 * - `NONE`: ni propias ni sin cliente.
 * - `UNKNOWN`: el pedido falló. No es «no hay cuentas»: el camino se ofrece y el modal de cuentas
 *   las vuelve a pedir.
 */
export function fromAccountsState(candidates, clientId, failed = false) {
    if (failed) return {kind: FROM_ACCOUNTS_UNKNOWN, count: 0, enabled: true};
    const accounts = (candidates && candidates.accounts) || [];
    const own = clientId
        ? accounts.filter(account => account.agency_client?.id === Number(clientId))
        : accounts;
    const ownFree = own.filter(account => !account.covered);
    const ownRunning = ownFree.filter(account => placementOf(account, {}) === PLACEMENT_MAIN);
    if (ownRunning.length) return {kind: FROM_ACCOUNTS_OWN, count: ownRunning.length, enabled: true};

    const unowned = clientId ? accounts.filter(account => !account.agency_client && !account.covered) : [];
    if (unowned.length) return {kind: FROM_ACCOUNTS_UNOWNED, count: unowned.length, enabled: true};
    if (ownFree.length) return {kind: FROM_ACCOUNTS_OWN, count: ownFree.length, enabled: true};

    return own.length
        ? {kind: FROM_ACCOUNTS_ALL_COVERED, count: own.length, enabled: false}
        : {kind: FROM_ACCOUNTS_NONE, count: 0, enabled: false};
}

/**
 * «¿Cómo quieres armar las líneas de seguimiento?» — el paso 0.
 *
 * Aparece cada vez que se crea una línea, y no una sola vez, porque la respuesta cambia con el
 * cliente: para uno conviene partir de las cuentas conectadas y para otro de su media plan.
 *
 * **Si hay un solo camino viable no se muestra:** preguntar entre una opción es hacerle dar un
 * click de más al usuario para llegar al único lugar al que podía ir. Un cliente sin cuentas
 * conectadas ve el camino automático apagado con el motivo, en vez de no verlo — que no esté
 * disponible es justamente lo que hay que explicar.
 */
export function NewLinePathModal({isOpen, toggle, clientId, clientName, mediaPlanAvailable, agencyClients,
                                  onPickManual, onCreated}) {
    const {t} = useTranslation();
    const [candidates, setCandidates] = useState(null);
    const [failed, setFailed] = useState(false);
    const [loaderDue, setLoaderDue] = useState(false);
    const [showAccounts, setShowAccounts] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setCandidates(null);
            setFailed(false);
            return;
        }
        fetchCandidates().then(response => {
            if (response?.status === 'OK') {
                setCandidates(response.data);
                return;
            }
            setFailed(true);
            setCandidates({accounts: []});
        });
    }, [isOpen]);

    useEffect(() => {
        setLoaderDue(false);
        if (!isOpen) return undefined;
        const timer = setTimeout(() => setLoaderDue(true), LOADER_DELAY_MS);
        return () => clearTimeout(timer);
    }, [isOpen]);

    const decided = candidates !== null;
    const fromAccounts = fromAccountsState(candidates, clientId, failed);
    // Con un solo camino no se pregunta: se toma. El automático nunca es el único (el manual está
    // siempre), así que esto sólo se dispara cuando el automático y el media plan no están. Un pedido
    // que falló NO cuenta como «sin cuentas»: ahí se muestran los caminos.
    const skip = decided && !fromAccounts.enabled && !mediaPlanAvailable;

    useEffect(() => {
        if (isOpen && skip) {
            toggle();
            onPickManual();
        }
    }, [isOpen, skip, toggle, onPickManual]);

    if (showAccounts) {
        return (
            <GlobalLinesFromAccountsModal
                isOpen
                toggle={() => {
                    setShowAccounts(false);
                    toggle();
                }}
                clientId={clientId}
                clientName={clientName}
                agencyClients={agencyClients}
                // Las cuentas ya se pidieron acá para decidir si este camino estaba disponible:
                // volver a pedirlas trae de nuevo TODAS las de la agencia (hay carteras de miles). Si
                // el pedido falló, el modal las pide de nuevo: es el reintento.
                candidates={failed ? null : candidates}
                startWithFinderOpen={fromAccounts.kind === FROM_ACCOUNTS_UNOWNED}
                onCreated={onCreated}/>
        );
    }

    // Nada que se vaya a deshacer: el modal no se dibuja si se va a saltear, ni mientras no se sabe
    // si se va a saltear (salvo que la espera ya se note). Con el media plan nunca se saltea.
    if (skip) return null;
    if (!decided && !mediaPlanAvailable && !loaderDue) return null;

    return (
        <Modal isOpen={isOpen} toggle={toggle} className="pz-modal">
            <ModalHeader toggle={toggle}>{t('path_zero.title')}</ModalHeader>
            <ModalBody>
                <div className="modal-section">
                    <p className="pz-lead">
                        {clientName
                            ? t('path_zero.lead_for_client', {client: clientName,
                                                              interpolation: {escapeValue: false}})
                            : t('path_zero.lead')}
                    </p>

                    {!decided ? (
                        <div className="box-loader"><Spinner color="positive"/></div>
                    ) : (
                        <div className="pz-paths">
                            <button type="button"
                                    className={`pz-path -featured${fromAccounts.enabled ? '' : ' -off'}`}
                                    onClick={() => fromAccounts.enabled && setShowAccounts(true)}
                                    disabled={!fromAccounts.enabled}>
                                <span className="pz-path__icon"><Plugs size={20}/></span>
                                <span className="pz-path__body">
                                    <span className="pz-path__title">{t('path_zero.from_accounts')}</span>
                                    <span className="pz-path__text">{fromAccountsText(t, fromAccounts, clientName)}</span>
                                </span>
                                {/* Sin saber qué cuentas hay (el pedido falló), no se recomienda nada. */}
                                {fromAccounts.enabled && fromAccounts.kind !== FROM_ACCOUNTS_UNKNOWN && (
                                    <span className="pz-path__tag">{t('path_zero.recommended')}</span>
                                )}
                                {fromAccounts.enabled && <CaretRight size={16} className="pz-path__caret"/>}
                            </button>

                            {/* La salida del estado «sin cuentas» va FUERA de la tarjeta apagada.
                                Adentro era inalcanzable: el navegador no despacha eventos de mouse
                                al subárbol de un control deshabilitado, así que el único enlace que
                                resolvía el problema no respondía al click. Lleva a las cuentas DEL
                                CLIENTE: lo que falta casi nunca es una conexión de la agencia sino
                                conectarle a este cliente cuentas a las que ya hay acceso. */}
                            {!fromAccounts.enabled && (
                                <Link className="pz-path__hint"
                                      to={clientId ? `/client/${clientId}/conections` : '/agency/conections'}
                                      onClick={toggle}>
                                    {t('path_zero.connect_first')}
                                </Link>
                            )}

                            {/* El asistente va SEGUNDO y ya no se llama «A mano»: se leía como un
                                trabajo pesado y no lo es; puede ser el camino más usado (QA de fede,
                                02/10). Por eso tampoco lleva el lápiz. */}
                            <button type="button" className="pz-path" onClick={() => {
                                toggle();
                                onPickManual();
                            }}>
                                <span className="pz-path__icon"><ListNumbers size={20}/></span>
                                <span className="pz-path__body">
                                    <span className="pz-path__title">{t('path_zero.manual')}</span>
                                    <span className="pz-path__text">{t('path_zero.manual_text')}</span>
                                </span>
                                <CaretRight size={16} className="pz-path__caret"/>
                            </button>

                            {/* Sin el módulo, el camino no se muestra apagado: se va. Ofrecer algo
                                que la agencia no puede contratar desde acá es ruido, no una venta. El
                                logo es el de Google Sheets: el media plan es una planilla de Google. */}
                            {mediaPlanAvailable && (
                                <Link className="pz-path" to={clientId ? `/client/${clientId}/media-plan`
                                                                       : '/agency/clients'}
                                      onClick={toggle}>
                                    <span className="pz-path__icon"><GoogleSheetsLogo size={22}/></span>
                                    <span className="pz-path__body">
                                        <span className="pz-path__title">{t('path_zero.from_media_plan')}</span>
                                        <span className="pz-path__text">{t('path_zero.from_media_plan_text')}</span>
                                    </span>
                                    <CaretRight size={16} className="pz-path__caret"/>
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            </ModalBody>
        </Modal>
    );
}

function fromAccountsText(t, state, clientName) {
    if (state.kind === FROM_ACCOUNTS_UNOWNED) {
        return t('path_zero.from_accounts_unowned_text', {count: state.count, client: clientName,
                                                          interpolation: {escapeValue: false}});
    }
    if (state.kind === FROM_ACCOUNTS_ALL_COVERED) return t('path_zero.from_accounts_all_covered');
    if (state.kind === FROM_ACCOUNTS_NONE) return t('path_zero.from_accounts_none');
    // La primera pantalla dice que algo falló, en vez de prometer lo de siempre (review C-rec-fe-linea-06).
    if (state.kind === FROM_ACCOUNTS_UNKNOWN) return t('path_zero.from_accounts_unknown');
    return t('path_zero.from_accounts_text', {count: state.count});
}
